import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Tooltip, Typography } from '@mui/material';

const GENDERS = [
  { key: 'male', label: 'Male', short: 'M', color: '#2E80DB' },
  { key: 'female', label: 'Female', short: 'F', color: '#E05297' },
  { key: 'other', label: 'Other', short: 'O', color: '#9AA0A6' },
];

const pct = (n, total) => (total ? Math.round((n / total) * 1000) / 10 : 0);

function StatCard({ data }) {
  const { gender } = useSelector((state) => state.filter);
  const counts = { male: data?.maleCount || 0, female: data?.femaleCount || 0, other: data?.otherCount || 0 };
  const hasGenders = data?.maleCount !== undefined || data?.femaleCount !== undefined || data?.otherCount !== undefined;
  // Average Age carries each gender's average age in the count fields, not a head count.
  const isAverage = data?.title === 'Average Age';
  const shown = GENDERS.filter((g) => !gender || gender === 'all' || gender === g.key);
  const total = shown.reduce((sum, g) => sum + counts[g.key], 0);

  return (
    <Box
      className="stat-card"
      sx={{
        textAlign: 'left',
        p: '14px 16px',
        borderRadius: '12px',
        border: '1px solid #ECECEC',
        boxShadow: '0 1px 2px rgba(16,24,40,0.04) !important',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: data.showIcon ? data.color || '#9AA0A6' : '#D0D0D0' }} />
        <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: '#6A7282', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          <span dangerouslySetInnerHTML={{ __html: data.title }} />
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111', lineHeight: 1.15 }}>
        {typeof data.value === 'number' ? data.value.toLocaleString() : data.value ?? '—'}
      </Typography>

      {/* Device: a breakdown list instead of genders */}
      {Array.isArray(data?.count) && (
        <Box display="flex" flexWrap="wrap" columnGap={1.5} rowGap={0.25}>
          {data.count.map((item) => (
            <Typography key={item._id} sx={{ fontSize: 12, color: '#5F6368' }}>
              {item._id || 'Unknown'} <b style={{ color: '#3C4043' }}>{item.count.toLocaleString()}</b>
            </Typography>
          ))}
        </Box>
      )}

      {!Array.isArray(data?.count) && hasGenders && isAverage && (
        <Box display="flex" gap={1.5}>
          {shown.map((g) => (
            <Typography key={g.key} sx={{ fontSize: 12, color: '#5F6368' }}>
              {g.label} <b style={{ color: '#3C4043' }}>{counts[g.key] ? Number(counts[g.key]).toFixed(1) : '—'}</b>
            </Typography>
          ))}
        </Box>
      )}

      {!Array.isArray(data?.count) && hasGenders && !isAverage && (
        <>
          {/* Share of each gender, as one bar */}
          <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: '#F1F3F4' }}>
            {total > 0 && shown.map((g) => counts[g.key] > 0 && (
              <Tooltip key={g.key} title={`${g.label}: ${counts[g.key].toLocaleString()} (${pct(counts[g.key], total)}%)`}>
                <Box sx={{ width: `${pct(counts[g.key], total)}%`, minWidth: 3, bgcolor: g.color }} />
              </Tooltip>
            ))}
          </Box>
          <Box display="flex" flexWrap="wrap" columnGap={1.5} rowGap={0.25}>
            {shown.map((g) => (
              <Tooltip key={g.key} title={`${pct(counts[g.key], total)}% ${g.label.toLowerCase()}`}>
                <Typography sx={{ fontSize: 12, color: '#5F6368', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: g.color }} />
                  {g.short} <b style={{ color: '#3C4043' }}>{counts[g.key].toLocaleString()}</b>
                </Typography>
              </Tooltip>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

export default StatCard;
